import React from 'react';
import { ClientStatus } from '../types';
import {
    WORKFLOW_STEPS,
    getClientStatusOrder,
    isWorkflowStepCompleted,
} from '../config/workflow';

interface WorkflowVisualizationProps {
    clientStatus: ClientStatus | undefined;
}

const WorkflowVisualization: React.FC<WorkflowVisualizationProps> = ({ clientStatus }) => {
    const currentStepOrder = getClientStatusOrder(clientStatus || 'draft');

    return (
        <div className="w-full bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Workflow Status</h3>
            <div className="relative pb-4">
                {/* Background Progress Line */}
                <div className="absolute top-6 left-0 right-0 h-1.5 bg-gray-200 rounded-full" />

                {/* Completed Progress Line */}
                {currentStepOrder > 0 && (() => {
                    const completedSteps = WORKFLOW_STEPS.filter(s => s.order > 0 && s.order <= currentStepOrder);
                    const gradientColors = completedSteps.map(s => s.color).join(', ');

                    return (
                        <div
                            className="absolute top-6 left-0 h-1.5 rounded-full transition-all duration-500"
                            style={{
                                width: `${(currentStepOrder / WORKFLOW_STEPS.length) * 100
                                    }% `,
                                background: completedSteps.length > 1
                                    ? `linear-gradient(to right, ${gradientColors})`
                                    : completedSteps[0]?.color || '#FBBF24'
                            }}
                        />
                    );
                })()}

                {/* Workflow Steps */}
                <div className="flex items-start justify-between">
                    {WORKFLOW_STEPS.map((step, index) => {
                        const isCompleted = isWorkflowStepCompleted(step.key, clientStatus);
                        const isCurrent = step.key === clientStatus;
                        const isPast = step.order < currentStepOrder;

                        return (
                            <div
                                key={step.key}
                                className="flex flex-col items-center relative z-10 flex-1"
                            >
                                {/* Step Circle Container - Fixed height to ensure all circles align */}
                                <div className="h-12 w-12 flex items-center justify-center mb-3">
                                    <div
                                        className={`
                                            w-12 h-12 rounded-full flex items-center justify-center
                                            text-lg font-semibold transition-all duration-300
                                            ${isCompleted || isPast
                                                ? 'bg-white border-2 shadow-md'
                                                : 'bg-gray-100 border-2 border-gray-300'
                                            }
                                            ${isCurrent
                                                ? 'ring-4 ring-offset-2'
                                                : ''
                                            }
                                        `}
                                        style={{
                                            borderColor: isCompleted || isPast ? step.color : undefined,
                                            color: isCompleted || isPast ? step.color : '#9CA3AF',
                                            transform: isCurrent ? 'scale(1.1)' : 'scale(1)',
                                            transformOrigin: 'center center',
                                        }}
                                    >
                                        {step.icon || step.order + 1}
                                    </div>
                                </div>

                                {/* Step Label */}
                                <div className="text-center max-w-[120px] min-h-[2.5rem]">
                                    <div
                                        className={`
                                            text-sm font-medium
                                            ${isCompleted || isPast ? 'text-gray-900' : 'text-gray-500'}
                                        `}
                                    >
                                        {step.displayName}
                                    </div>
                                    {isCurrent && (
                                        <div className="text-xs text-gray-500 mt-1">
                                            Current
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Current Step Description */}
            {clientStatus && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start gap-3">
                        <span className="text-2xl">
                            {WORKFLOW_STEPS.find(s => s.key === clientStatus)?.icon}
                        </span>
                        <div>
                            <div className="font-semibold text-gray-900">
                                {WORKFLOW_STEPS.find(s => s.key === clientStatus)?.displayName}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                                {WORKFLOW_STEPS.find(s => s.key === clientStatus)?.description}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkflowVisualization;

